import React from 'react';
import {
  DataGrid,
  GridColDef,
  GridRowsProp,
} from '@mui/x-data-grid';
import { Paper, Typography, Box, Chip, Alert } from '@mui/material';
import { Wrestler } from '../hooks/useCsvImport';

interface WrestlerTableProps {
  wrestlers: Wrestler[];
}

const WrestlerTable: React.FC<WrestlerTableProps> = ({ wrestlers }) => {
  // Count wrestlers with missing last names (including ALLCAPS school fillers)
  const missingLastNameCount = wrestlers.filter(w => !w.lastName || w.lastName.trim() === '').length;
  const schoolNameFillerCount = wrestlers.filter(w => 
    w.lastName && w.lastName === w.lastName.toUpperCase() && w.lastName.length > 3
  ).length;

  const columns: GridColDef[] = [
    {
      field: 'firstName',
      headerName: 'First Name',
      width: 150,
      editable: true,
    },
    {
      field: 'lastName',
      headerName: 'Last Name',
      width: 150,
      editable: true,
      renderCell: (params) => {
        const lastName = params.value;
        if (!lastName || lastName.trim() === '') {
          return (
            <Chip 
              label="Missing" 
              size="small" 
              color="warning" 
              variant="outlined"
            />
          );
        }
        // Check if it's an ALLCAPS school name filler
        if (lastName === lastName.toUpperCase() && lastName.length > 3) {
          return (
            <Chip 
              label={lastName} 
              size="small" 
              color="info" 
              variant="outlined"
            />
          );
        }
        return lastName;
      },
    },
    {
      field: 'weightKg',
      headerName: 'Weight (kg)',
      width: 120,
      type: 'number',
      editable: true,
    },
    {
      field: 'age',
      headerName: 'Age',
      width: 100,
      type: 'number',
      editable: true,
      valueGetter: (params) => params.row.age || '-',
    },
    {
      field: 'grade',
      headerName: 'Grade',
      width: 100,
      editable: true,
      valueGetter: (params) => params.row.grade || '-',
    },
  ];

  const rows: GridRowsProp = wrestlers.map((wrestler) => ({
    id: wrestler.id,
    firstName: wrestler.firstName,
    lastName: wrestler.lastName,
    weightKg: wrestler.weightKg,
    age: wrestler.age,
    grade: wrestler.grade,
  }));

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Imported Wrestlers ({wrestlers.length})
      </Typography>
      
      {missingLastNameCount > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {missingLastNameCount} wrestler(s) have missing last names. You can edit them directly in the table.
        </Alert>
      )}
      
      {schoolNameFillerCount > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {schoolNameFillerCount} wrestler(s) have school names used as last name placeholders. You can edit them directly in the table.
        </Alert>
      )}
      
      <Box sx={{ height: 400, width: '100%' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          pageSizeOptions={[10, 25, 30, 50]}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 30 },
            },
          }}
          disableRowSelectionOnClick
        />
      </Box>
    </Paper>
  );
};

export default WrestlerTable;
